import './App.css';
import './driverjs.css';
import { Suspense} from 'react';
import { AppDataProvider } from 'modules/main/context/appData';
import { Route, Routes, BrowserRouter, Outlet } from 'react-router-dom';
import Catalog from 'modules/catalog/pages/catalog';
import CatalogEntry from 'modules/catalog/pages/catalogEntry';
import { LandingPage } from 'modules/main/pages/landing';
import { CartProvider } from 'modules/cart/context/cartContext';
import { Search } from 'modules/catalog/pages/search';
import { ProductPage } from 'modules/product/pages/product';
import { AuthProvider } from 'modules/authentication/context/auth-provider';
import { ConditionalProtectedRoute } from 'modules/authentication/components/conditional-protected-route';
import { SettingsPage } from 'modules/settings/pages/settings-page';
import { ExtensionSyncProvider } from 'modules/checkout/components/extension-sync';
import { Footer } from 'modules/main/components/footer';
import { PrivacyTerms } from 'modules/main/pages/privacy-terms';
import { ServiceTerms } from 'modules/main/pages/service-terms';
import { PostHogErrorBoundary } from 'posthog-js/react';
import { LoginPage } from 'modules/authentication/pages/login-page';
import { MainPage } from 'modules/main/pages/main';
import { CartCreation } from 'modules/cartCreation/pages/cartCreation';
import { ListsProvider } from 'modules/lists/context/ListsContext';

import { ListDetailPage } from 'modules/lists/pages/ListDetailPage';
import { Optimizing } from 'modules/checkout/pages/Optimizing';
import { TooltipProvider } from '@/components/ui/tooltip';
import { ScenarioResults } from 'modules/checkout/components/ScenarioResults';
import { ViewOrder } from 'modules/checkout/pages/viewOrder';
import { Toaster } from '@/components/ui/sooner';
import { PwaInstallProvider } from 'modules/main/context/pwa-install-provider';
import { VersionPollingProvider } from 'modules/main/context/version-polling-provider';
import { useVersionPolling } from 'modules/main/hooks/useVersionPolling';
import { UpdateAvailableDialog } from 'modules/main/components/update-available-dialog';
import { SidebarProvider } from '@/components/ui/sidebar';
import { SubscriptionConfirmationPage } from 'modules/subscription/pages/confirmation';
import { SubscriptionErrorPage } from 'modules/subscription/pages/error';
import { SubscriptionAlreadyActivePage } from 'modules/subscription/pages/alreadyActive';
import { OnboardingPage } from 'modules/onboarding/pages/OnboardingPage';
import MainLayout from 'modules/main/components/main-layout';

/**
 * Internal component that reads the version polling context and
 * conditionally renders the update dialog.
 */
const VersionUpdateCheck = () => {
  const { isUpdateAvailable, newVersion } = useVersionPolling();

  return (
    <UpdateAvailableDialog
      open={isUpdateAvailable}
      onOpenChange={() => {
        // Dialog is non-dismissible; close is blocked internally
      }}
      newVersion={newVersion}
    />
  );
};


function App() {
  
  return (
    <div className="grid min-h-[100dvh] grid-rows-[auto,1fr,auto]">
    <PostHogErrorBoundary fallback={(error) => (
      <div className="flex min-h-screen items-center justify-center p-4">
        <div className="max-w-md w-full bg-red-50 border border-red-200 rounded-lg p-6">
          <h2 className="text-lg font-semibold text-red-800 mb-2">
            ¡Ups! Algo salió mal
          </h2>
          <p className="text-red-600 mb-4">
            Ha ocurrido un error inesperado en la aplicación.
          </p>
        </div>
      </div>
    )}>
      
      <Suspense fallback="Cargando">     
      <BrowserRouter>
        <VersionPollingProvider>
        <PwaInstallProvider>
        <TooltipProvider>         
        <Routes>          
          <Route path="/privacy-policy" element={<PrivacyTerms/>}/>
          <Route path="/terms-of-service" element={<ServiceTerms/>}/>    
          <Route path="/*" element={
            <AuthProvider>
              <Routes>
                {/* Rutas públicas — sin SidebarProvider porque no usan sidebar */}
                <Route path="/" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <><LandingPage/><Footer/></>
                  </ConditionalProtectedRoute>
                }/>
                <Route path="/onboarding" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <OnboardingPage/>
                  </ConditionalProtectedRoute>
                }/>
                <Route path="/login" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <LoginPage/>
                  </ConditionalProtectedRoute>
                }/>

                {/* Rutas de suscripción - públicas, sin auth ni sidebar */}
                <Route path="/subscription/confirmation" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <SubscriptionConfirmationPage/>
                  </ConditionalProtectedRoute>
                }/>
                <Route path="/subscription/error" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <SubscriptionErrorPage/>
                  </ConditionalProtectedRoute>
                }/>
                <Route path="/subscription/already-active" element={
                  <ConditionalProtectedRoute requireAuth={false}>
                    <SubscriptionAlreadyActivePage/>
                  </ConditionalProtectedRoute>
                }/>

                {/* Rutas que requieren auth — el guard envuelve TODO el provider tree
                    para que AppDataProvider, CartProvider, ListsProvider etc. NO
                    monten ni disparen llamadas API antes de que se confirme la sesión.
                    Esto evita que un 401 de esos providers dispare auth:logout y
                    desvie el redirect a /login hacia /. */}
                <Route path="*" element={
                  <ConditionalProtectedRoute requireAuth={true}>
                    <ExtensionSyncProvider>
                      <AppDataProvider>
                        <CartProvider>
                          <SidebarProvider
                            className="min-w-0"
                            style={
                            {
                              "--sidebar-width": "18rem",
                              "--header-height": "3rem",
                            } as React.CSSProperties }
                          >
                          <Routes>
                            <Route element={<ListsProvider><MainLayout /></ListsProvider>}>
                              <Route path="/store" element={<MainPage/>} />
                              <Route
                                path="/lists"
                                element={<Outlet />}
                              >
                                <Route path=":listUuid" element={<ListDetailPage />} />
                              </Route>
                              <Route path="/:p" element={<ProductPage />} />
                              <Route path="/catalog" element={<CatalogEntry/>} />
                              <Route path="/catalog/:section" element={<Catalog/>} />
                              <Route path="/search/:q" element={<Search/>} />
                              <Route path="/cart/calculate" element={<Optimizing/>} />
                              <Route path="/cart/create_cart" element={<CartCreation/>} />
                              <Route path="/cart/results/:order_id/" element={<ScenarioResults/>} />
                              <Route path="/cart/results/:order_id/:scenario_id/:store_id" element={<ViewOrder/>} />
                               <Route path="/cart/results/:order_id/:scenario_id" element={<ViewOrder/>} />
                               <Route path="/settings" element={<SettingsPage/>} />
                               <Route path="/settings/account" element={<SettingsPage section='account'/>} />
                               <Route path="/settings/preferences" element={<SettingsPage section='preferences'/>} />
                               <Route path="/settings/orders" element={<SettingsPage section='history'/>} />
                               <Route path="/settings/subscription" element={<SettingsPage section='subscription'/>} />
                             </Route>
                           </Routes>
                          </SidebarProvider>
                        </CartProvider>
                      </AppDataProvider>
                    </ExtensionSyncProvider>
                  </ConditionalProtectedRoute>
                }/>
              </Routes>
            </AuthProvider>

          }/>
        </Routes>

        
        </TooltipProvider>
        <Toaster position='top-center'/>
        </PwaInstallProvider>
        <VersionUpdateCheck />
        </VersionPollingProvider>
      </BrowserRouter> 
      </Suspense>
    </PostHogErrorBoundary>
  
    </div>
  );
}

export default App;
